import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios, {AxiosError} from "axios";
import { handleApiError } from "@/utils/apiErrorHandler";
import { clearError } from "./errorSlice";

// Helper function to build query string with pagination options
export const buildQueryString = (query: any, options: { includePagination?: boolean } = { includePagination: true }): string => {
  const params = new URLSearchParams();
  
  // Only include pagination parameters if specified
  if (options.includePagination) {
    if (query.page !== undefined) params.append("page", query.page);
    if (query.size !== undefined) params.append("size", query.size);
  }
  
  // Always include filter parameters
  if (query.version !== undefined) params.append("version", query.version);
  if (query.designation !== undefined) params.append("designation", query.designation);
  if (query.premiumSpecialty !== undefined) params.append("premiumSpecialty", query.premiumSpecialty);
  if (query.geographicArea !== undefined) params.append("geographicArea", query.geographicArea);
  if (query.search !== undefined) params.append("fullName", query.search);
  
  return params.toString();
};

// Define fetchGroupSummaryFilter
export const fetchGroupSummaryFilter = createAsyncThunk(
  "resultsByPhysician/fetchGroupSummaryFilter",
  async ({ groupId, query }: { groupId: string; query: any }, { dispatch }) => {
    try {
      // Clear any previous errors from this slice when starting a new request
      dispatch(clearError());
      
      // Exclude pagination parameters for summary filter
      const queryString = buildQueryString(query, { includePagination: false });
      const response = await axios.get(
        `/papi/secure/group/${groupId}/summary?isDesignated=true&${queryString}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "resultsByPhysician", dispatch);
      throw error;
    }
  }
);

// Define fetchGroupPhysicians
export const fetchGroupPhysicians = createAsyncThunk(
  "resultsByPhysician/fetchGroupPhysicians",
  async ({ groupId, query }: { groupId: string; query: any }, { dispatch }) => {
    try {
      // Clear any previous errors from this slice when starting a new request
      dispatch(clearError());
      
      // Include pagination parameters for physicians list
      const queryString = buildQueryString(query, { includePagination: true });
      const response = await axios.get(
        `/papi/secure/groups/physicians?groupID=${groupId}&isDesignated=true&eligible=true&${queryString}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "resultsByPhysician", dispatch);
      throw error;
    }
  }
);

export const fetchGroupInfo = createAsyncThunk(
  "resultsByPhysician/fetchGroupInfo",
  async (groupId: string, { dispatch }) => {
    try {
      dispatch(clearError());

      const response = await axios.get(
        `/papi/secure/groups/info?groupID=${groupId}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "resultsByPhysician", dispatch);
      throw error;
    }
  }
);

export const fetchGroupsByUser = createAsyncThunk(
  "resultsByPhysician/fetchGroupsByUser",
  async (userId: string, { dispatch }) => {
    try {
      dispatch(clearError());

      const response = await axios.get(
        `/papi/secure/groups/by-user?userId=${userId}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "resultsByPhysician", dispatch);
      throw error;
    }
  }
);

const initialState = {
  groupSummaryFilters: {
    data: [],
    currentPage: 0,
    previousQuery: {},
  },
  groupPhysicians: {
    data: [],
    totalItems: 0,
    currentPage: 0,
    previousQuery: {},
  },
  groupInfo: null, // Add groupInfo to the state
  userGroups: [], // Add userGroups to the state
  loading: false,
  error: null as string | null,
};

const resultsByPhysicianSlice = createSlice({
  name: "resultsByPhysician",
  initialState,
  reducers: {
    // Define reducers if needed
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroupSummaryFilter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupSummaryFilter.fulfilled, (state, action) => {
        state.loading = false;
        state.groupSummaryFilters = {
          data: action.payload.data,
          currentPage: action.payload.currentPage,
          previousQuery: action.meta.arg.query,
        };
      })
      .addCase(fetchGroupSummaryFilter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchGroupPhysicians.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupPhysicians.fulfilled, (state, action) => {
        state.loading = false;
        state.groupPhysicians = {
          data: action.payload.data,
          totalItems: action.payload.totalItems,
          currentPage: action.payload.currentPage,
          previousQuery: action.meta.arg.query,
        };
      })
      .addCase(fetchGroupPhysicians.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchGroupInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.groupInfo = action.payload.data; // Update groupInfo in the state
      })
      .addCase(fetchGroupInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(fetchGroupsByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupsByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.userGroups = action.payload.data; // Update userGroups in the state
      })
      .addCase(fetchGroupsByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      });
  },
});

export default resultsByPhysicianSlice.reducer;
